import { Copy, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { useStoredState } from "@/hooks/useStoredState";
import { AutoTextarea, TextField } from "@/components/tools/fields";
import { LETTERS, buildLetter } from "@/data/letters";

const INIT = { details: {}, tpl: "concern", edits: {} };
const FIELDS = [
  { k: "parent", label: "Parent / carer name" },
  { k: "sendco", label: "SENDCO name" },
  { k: "school", label: "School name" },
  { k: "date", label: "Meeting date", type: "date" },
  { k: "time", label: "Meeting time", type: "time" },
  { k: "place", label: "Location" },
];

export const LettersBlock = () => {
  const [data, setData] = useStoredState("parent-letters", INIT);
  const tpl = LETTERS.find((l) => l.id === data.tpl) || LETTERS[0];
  const text = data.edits?.[tpl.id] ?? buildLetter(tpl, data.details || {});
  const setDetail = (k, v) => setData((d) => ({ ...d, details: { ...d.details, [k]: v } }));
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Letter copied. Paste it into your own letterhead.");
    } catch {
      toast.error("Could not copy. Please select the text and copy it manually.");
    }
  };
  return (
    <div>
      <div className="no-print grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {FIELDS.map((f) => (
          <TextField key={f.k} id={`pl-${f.k}`} label={f.label} type={f.type} value={data.details?.[f.k]}
            onChange={(v) => setDetail(f.k, v)} testid={`pl-field-${f.k}`} />
        ))}
      </div>
      <div className="no-print mt-6 flex flex-wrap gap-2" role="tablist" aria-label="Letter templates">
        {LETTERS.map((l) => (
          <button key={l.id} type="button" role="tab" aria-selected={tpl.id === l.id} aria-controls="pl-letter"
            onClick={() => setData((d) => ({ ...d, tpl: l.id }))} data-testid={`pl-tab-${l.id}`}
            className={`pill-opt ${tpl.id === l.id ? "is-on tone-brand" : ""}`}>
            {l.label}
          </button>
        ))}
      </div>
      <div id="pl-letter" role="tabpanel" className="mt-5 rounded-2xl border-2 border-line bg-surface p-4 sm:p-6">
        <AutoTextarea id="pl-text" label={`${tpl.label} (you can edit this wording)`} value={text} rows={12}
          onChange={(v) => setData((d) => ({ ...d, edits: { ...d.edits, [tpl.id]: v } }))} testid="pl-letter-text" />
        <div className="no-print mt-4 flex flex-wrap gap-2">
          <button type="button" className="btn btn-brand btn-sm" onClick={copy} data-testid="pl-copy"><Copy size={14} aria-hidden /> Copy letter</button>
          {data.edits?.[tpl.id] != null && (
            <button type="button" className="btn btn-ghost btn-sm" data-testid="pl-reset-wording"
              onClick={() => setData((d) => { const e = { ...d.edits }; delete e[tpl.id]; return { ...d, edits: e }; })}>
              <RotateCcw size={14} aria-hidden /> Restore original wording
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
